import {useContext} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {CareJourneyAttachmentType, CareJourneyDetailsResponse} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {NEW_RESOURCE} from '@/helpers/search_params';
import UploadModal from '@common/UploadModal';
import Helpers from '@/helpers/helpers';

type Props = {
  isOpen: boolean;
  close(): void;
  careJourneyDetails: CareJourneyDetailsResponse;
}

export default function UploadAttachmentModal({ isOpen, close, careJourneyDetails }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentUser } = useAuthStore();
  const currentRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (formData: FormData) =>
      carecoApi?.createCareJourneyAttachment(careJourneyDetails.careJourneyInstanceId, currentRoster!.id, formData),
    onSuccess(_, formData) {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Care journey attachment uploaded successfully.',
      });
      optimisticCreateCareJourneyAttachment(formData.get('file') as File);
      void queryClient.invalidateQueries({ queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId] });
      close();
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to upload care journey attachment. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const optimisticCreateCareJourneyAttachment = (file: File) => {
    queryClient.setQueryData<CareJourneyDetailsResponse>(
      ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      (careJourneyDetails) => {
        if (careJourneyDetails) {
          return {
            ...careJourneyDetails,
            attachments: [
              ...careJourneyDetails.attachments,
              {
                id: NEW_RESOURCE,
                s3Key: file.name,
                type: CareJourneyAttachmentType.USER_UPLOAD,
                createdBy: currentUser!,
                createdAt: new Date().toISOString(),
              },
            ],
          };
        }
      });
  };

  return (
    <UploadModal
      isOpen={isOpen}
      isPending={isPending}
      close={close}
      confirm={mutate}
    />
  );
}
